import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// ErrorState
//
// Shown when a data query FAILS — distinct from EmptyState (no data).
// WP20 (S-147/S-112/S-138): API errors must not masquerade as empty
// onboarding states; the user gets a clear message and a way to retry.
// ─────────────────────────────────────────────

interface ErrorStateProps {
  title?: string;
  description?: string;
  /** When provided, renders a Retry button that re-runs the failed query. */
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title,
  description,
  onRetry,
  className,
}: ErrorStateProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("errors.loadFailedTitle");
  const resolvedDescription = description ?? t("errors.loadFailedDescription");

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-4 px-6 py-12 text-center",
        className
      )}
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-danger-bg">
        <AlertTriangle className="h-8 w-8 text-danger" />
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-[18px] font-semibold text-text-primary">{resolvedTitle}</h3>
        {resolvedDescription && (
          <p className="max-w-sm text-body text-text-secondary">{resolvedDescription}</p>
        )}
      </div>

      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 flex items-center gap-2 rounded-control px-4 py-2 text-[14px] font-medium text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: "var(--accent)" }}
        >
          {t("common.retry")}
        </button>
      )}
    </div>
  );
}
